import { useEffect, useMemo, useState } from "react";
import { X, Pencil, Check } from "lucide-react";
import { FormSection } from "../ui/FormSection";
import {
  fetchChannelQuestions,
  fetchChannelQuestionState,
  setQuestionSelection,
  addReportQuestion,
  removeReportQuestion,
  renameReportQuestion,
  suggestReportQuestions,
} from "../../api/reportApi";
import type { AIProvider } from "../../types";

// Owner-only "Manage questions" editor, on the Reports screen itself -
// one list that both manages this channel's stored question history
// (add/edit/delete/suggest) and lets the owner pick which of them to ask
// right now via the checkbox + "Ask questions" button at the bottom.
// Previously these were two separate, nearly-identical lists (a plain
// "ask now" checklist on Reports, management split off into AI Settings)
// which was confusing - see conversation that asked "how do I ask all
// questions, with what buttons?". Every question lives in the channel's
// stored list on the backend - all of them editable and removable. (This used
// to have a synthetic
// always-offered entry (the "explain this chart" quick question) that
// isn't part of the stored history and has no edit/delete controls.
export function ChannelQuestionsSection({
  nickname,
  provider,
  apiKey,
  onAsk,
  asking,
}: {
  nickname: string;
  provider: AIProvider;
  apiKey?: string;
  onAsk: (questions: string[]) => void;
  asking: boolean;
}) {
  const [questions, setQuestions] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [suggesting, setSuggesting] = useState(false);
  const [editing, setEditing] = useState<{
    original: string;
    draft: string;
  } | null>(null);
  const displayedQuestions = useMemo(() => questions ?? [], [questions]);
  // Which questions are switched OFF for the next "Ask questions" run -
  // stored per channel on the backend (so web and iOS agree), kept as the
  // unchecked ones so every new/suggested question starts checked.
  const [unchecked, setUncheckedState] = useState<string[]>([]);
  const setUnchecked = (next: string[]) => {
    const before = unchecked;
    setUncheckedState(next);
    setQuestionSelection(nickname, next).catch((err) => {
      setUncheckedState(before);
      setError(err instanceof Error ? err.message : "Could not save the selection.");
    });
  };
  const selected = displayedQuestions.filter((q) => !unchecked.includes(q));
  const toggle = (q: string) =>
    setUnchecked(unchecked.includes(q) ? unchecked.filter((x) => x !== q) : [...unchecked, q]);
  const allChecked = displayedQuestions.length > 0 && selected.length === displayedQuestions.length;

  useEffect(() => {
    setQuestions(null);
    fetchChannelQuestionState(nickname)
      .then((state) => {
        setQuestions(state.questions);
        setUncheckedState(state.unchecked);
      })
      .catch((err) =>
        setError(
          err instanceof Error ? err.message : "Could not load questions.",
        ),
      );
  }, [nickname]);

  const add = async () => {
    const q = draft.trim();
    if (!q || questions?.includes(q)) return;
    setError(null);
    try {
      setQuestions(await addReportQuestion(nickname, q));
      setDraft("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not add question.");
    }
  };

  const remove = async (q: string) => {
    setError(null);
    try {
      setQuestions(await removeReportQuestion(nickname, q));
      setUncheckedState((prev) => prev.filter((x) => x !== q));
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Could not remove question.",
      );
    }
  };

  const startEdit = (q: string) => setEditing({ original: q, draft: q });

  const saveEdit = async () => {
    if (!editing) return;
    const next = editing.draft.trim();
    if (!next || next === editing.original) {
      setEditing(null);
      return;
    }
    setError(null);
    try {
      setQuestions(
        await renameReportQuestion(nickname, editing.original, next),
      );
      setUncheckedState((prev) => prev.map((x) => (x === editing.original ? next : x)));
      setEditing(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save the edit.");
    }
  };

  const suggest = async () => {
    setSuggesting(true);
    setError(null);
    try {
      await suggestReportQuestions(nickname, {
        provider,
        periodDays: 30,
        apiKey,
      });
      setQuestions(await fetchChannelQuestions(nickname));
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Could not suggest questions.",
      );
    } finally {
      setSuggesting(false);
    }
  };

  return (
    <FormSection
      title="Manage questions"
      collapsible
      id="report-manage-questions"
      defaultOpen
    >
      <div className="flex items-center gap-2">
        {displayedQuestions.length > 0 && (
          <button
            type="button"
            onClick={() => setUnchecked(allChecked ? displayedQuestions : [])}
            className="text-[11px] text-blue-600 hover:underline shrink-0"
          >
            {allChecked ? "Select none" : "Select all"}
          </button>
        )}
        <span className="flex-1" />
        <button
          type="button"
          onClick={suggest}
          disabled={suggesting}
          className="text-[11px] text-blue-600 hover:underline disabled:opacity-50 shrink-0"
        >
          {suggesting
            ? "Thinking…"
            : questions?.length
              ? "Ask AI again"
              : "Suggest questions"}
        </button>
      </div>
      {error && <p className="text-xs text-red-500 m-0">{error}</p>}
      {!questions && !error && (
        <p className="text-xs text-black/40 dark:text-white/40 m-0">Loading…</p>
      )}
      {questions && (
        <ul className="flex flex-col gap-1">
          {displayedQuestions.map((q, idx) => {
            return (
              <li key={q} className="flex items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={!unchecked.includes(q)}
                  onChange={() => toggle(q)}
                  aria-label={`Ask question ${idx + 1}`}
                  className="mt-1 shrink-0 cursor-pointer"
                />
                <span className="shrink-0 w-5 text-right tabular-nums text-black/40 dark:text-white/40">
                  {idx + 1}.
                </span>
                {editing?.original === q ? (
                  <>
                    <input
                      value={editing.draft}
                      onChange={(e) =>
                        setEditing({ original: q, draft: e.target.value })
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          saveEdit();
                        }
                        if (e.key === "Escape") setEditing(null);
                      }}
                      autoFocus
                      className="flex-1 rounded-lg border border-black/15 dark:border-white/15 bg-white dark:bg-white/5 px-2 py-1 text-sm outline-none focus:border-blue-500"
                    />
                    <button
                      type="button"
                      onClick={saveEdit}
                      aria-label="Save edit"
                      className="text-black/40 dark:text-white/40 hover:text-blue-600 shrink-0"
                    >
                      <Check size={14} />
                    </button>
                  </>
                ) : (
                  <>
                    <span className="flex-1">{q}</span>
                    <>
                      <button
                        type="button"
                        onClick={() => startEdit(q)}
                        aria-label={`Edit "${q}"`}
                        className="text-black/40 dark:text-white/40 hover:text-blue-600 shrink-0"
                      >
                        <Pencil size={13} />
                      </button>
                      <button
                        type="button"
                        onClick={() => remove(q)}
                        aria-label={`Remove "${q}"`}
                        className="text-black/40 dark:text-white/40 hover:text-red-500 shrink-0"
                      >
                        <X size={13} />
                      </button>
                    </>
                  </>
                )}
              </li>
            );
          })}
        </ul>
      )}
      <div className="flex gap-2">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
          placeholder="Add a question"
          className="flex-1 min-w-0 rounded-lg border border-black/15 dark:border-white/15 bg-white dark:bg-white/5 px-3 py-1.5 text-sm outline-none focus:border-blue-500"
        />
        <button
          type="button"
          onClick={add}
          disabled={!draft.trim()}
          className="rounded-lg px-3 py-1.5 border border-black/15 dark:border-white/20 text-sm disabled:opacity-50 shrink-0"
        >
          Add
        </button>
      </div>
      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => onAsk(selected)}
          disabled={asking || selected.length === 0}
          className="rounded-lg px-4 py-2 bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
        >
          {asking
            ? "Queuing…"
            : selected.length === displayedQuestions.length || selected.length === 0
              ? "Ask questions"
              : `Ask ${selected.length} selected`}
        </button>
      </div>
    </FormSection>
  );
}
